import React, { forwardRef, useState, useEffect, useRef, useMemo, useCallback, useImperativeHandle } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList } from "@fortawesome/free-solid-svg-icons";
import { appendFromDashboardQuery } from "../../../utils/practiceNavigation";
import { toast } from "react-toastify";
import practiceService from "../../../services/practiceService";
import patientService from "../../../services/patientService";
import { setUsePatientScheduleTargetsPreference } from "../../../utils/patientScheduleTargetPreference.js";
import { parsePatientVitals } from "../../../components/MeasurementData/VitalsTabs/VitalsTabs";
import { PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE } from "../../../utils/practiceMonitoringUtils";
import PageLoadingSpinner from "../../../components/PageLoadingSpinner/PageLoadingSpinner";
import ApplyScheduleTargetsToPatientsModal, {
  APPLY_OPTIONS
} from "./ApplyScheduleTargetsToPatientsModal";
import "./ScheduleTargetSettings.css";




// --- Helper Functions ---
const convertTimeTo12Hour = (timeStr) => {
  if (!timeStr) return "";
  if (timeStr.includes("AM") || timeStr.includes("PM")) return timeStr;
  
  // Handle HH:mm or HH:mm:ss
  const parts = timeStr.split(":");
  let h = parseInt(parts[0]);
  const minutes = parts[1] || "00";
  const period = h >= 12 ? "PM" : "AM";
  if (h > 12) h -= 12;
  if (h === 0) h = 12;
  return `${String(h).padStart(2, "0")}:${minutes.substring(0, 2)} ${period}`;
};

const convertTimeTo24Hour = (timeStr) => {
  if (!timeStr) return "";
  // If already HH:mm or HH:mm:ss, normalize to HH:mm
  if (timeStr.match(/^\d{1,2}:\d{2}(:\d{2})?$/)) {
    const [h, m] = timeStr.split(":");
    return `${String(h).padStart(2, "0")}:${m.substring(0, 2)}`;
  }
  
  if (timeStr.includes("AM") || timeStr.includes("PM")) {
    const parts = timeStr.trim().split(" ");
    if (parts.length < 2) return timeStr;
    const time = parts[0];
    const period = parts[1].toUpperCase();
    const [hours, minutes] = time.split(":");
    let h = parseInt(hours);
    if (period === "PM" && h !== 12) h += 12;
    if (period === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${minutes.substring(0, 2) || "00"}`;
  }
  return timeStr;
};

const normalizeNumericValue = (value, fallback = "") => {
  if (value === null || value === undefined || value === "") return fallback;
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const deepEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Normalize a row's `name`/`label` for matching DB rows against React defaults.
// We trim and lowercase so that case/whitespace drift between the master table
// and the hard-coded defaults doesn't silently break the merge.
const normalizeName = (value) => String(value ?? '').trim().toLowerCase();

// Build a `name -> row` lookup from an array of DB rows. Used by the load /
// reload code paths to overlay DB values on top of the fixed React-default
// list while keeping the visible row count stable.
const mapByName = (rows) => {
  const map = new Map();
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    const key = normalizeName(r?.name ?? r?.label);
    if (key) map.set(key, r);
  });
  return map;
};

// Custom Time Input that displays AM/PM
const TimeInputAMPM = ({ value, onChange, className, disabled = false }) => {
  const timeInputRef = useRef(null);
  const displayInputRef = useRef(null);
  const [displayValue, setDisplayValue] = useState("");

  useEffect(() => {
    if (value) {
      const formatted = convertTimeTo12Hour(value);
      setDisplayValue(formatted);
    } else {
      setDisplayValue("");
    }
  }, [value]);

  const handleDisplayClick = () => {
    if (disabled) return;
    if (timeInputRef.current) {
      timeInputRef.current.showPicker?.();
      timeInputRef.current.focus();
      timeInputRef.current.click();
    }
  };

  const handleTimeChange = (e) => {
    if (disabled) return;
    const newValue = e.target.value;
    setDisplayValue(convertTimeTo12Hour(newValue));
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div className={`time-input-wrapper-custom${disabled ? ' is-disabled' : ''}`} style={{ position: 'relative', width: '100%' }}>
      <input
        ref={displayInputRef}
        type="text"
        className={className}
        value={displayValue}
        readOnly
        disabled={disabled}
        onClick={handleDisplayClick}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          paddingRight: '30px'
        }}
      />
      <input
        ref={timeInputRef}
        type="time"
        value={value || ""}
        onChange={handleTimeChange}
        disabled={disabled}
        style={{
          position: 'absolute',
          opacity: 0,
          width: '100%',
          height: '100%',
          cursor: disabled ? 'not-allowed' : 'pointer',
          zIndex: 2,
          pointerEvents: disabled ? 'none' : 'auto'
        }}
      />
    </div>
  );
};

const formatDailyCardLabel = (name) =>
  String(name || "")
    .replace(/\s*Time\s*$/i, "")
    .trim();

/** One target row per BG period (keeps the single-table UI values independent). */
const DEFAULT_BG_TARGETS = [
  { id: null, label: "Wake-up Target Rangeup Time", min: 60, max: 110 },
  { id: null, label: "Before-Breakfast Target Range", min: 60, max: 110 },
  { id: null, label: "After-Breakfast Target Range", min: 70, max: 140 },
  { id: null, label: "Before-Lunch Target Range", min: 60, max: 110 },
  { id: null, label: "After-Lunch Target Range", min: 70, max: 140 },
  { id: null, label: "Before-Dinner Target Range", min: 60, max: 110 },
  { id: null, label: "After-Dinner Target Range", min: 70, max: 140 },
  { id: null, label: "Bedtime Target Range", min: 60, max: 110 },
  { id: null, label: "Midnight Target Range", min: 60, max: 110 },
];

/** Prefer period-specific DB rows; fall back to legacy Before-Meal / After-Meal. */
const BG_TARGET_NAME_FALLBACKS = {
  "wake-up target rangeup time": ["wake-up target rangeup time", "wake-up target range"],
  "before-breakfast target range": ["before-breakfast target range", "before-meal target range"],
  "after-breakfast target range": ["after-breakfast target range", "after-meal target range"],
  "before-lunch target range": ["before-lunch target range", "before-meal target range"],
  "after-lunch target range": ["after-lunch target range", "after-meal target range"],
  "before-dinner target range": ["before-dinner target range", "before-meal target range"],
  "after-dinner target range": ["after-dinner target range", "after-meal target range"],
  "bedtime target range": ["bedtime target range"],
  "midnight target range": ["midnight target range"],
};

const resolveBgTargetFromDb = (label, dbByName) => {
  const preferred = normalizeName(label);
  const keys = BG_TARGET_NAME_FALLBACKS[preferred] || [preferred];
  for (let i = 0; i < keys.length; i += 1) {
    const dbItem = dbByName.get(keys[i]);
    if (dbItem) return { dbItem, exact: i === 0 };
  }
  return null;
};

const mergeBgTargetsFromDb = (defaults, dbRows) => {
  const dbByName = mapByName(dbRows);
  return defaults.map((defaultItem) => {
    const resolved = resolveBgTargetFromDb(defaultItem.label, dbByName);
    if (!resolved) return { ...defaultItem };
    const { dbItem, exact } = resolved;
    return {
      ...defaultItem,
      // Only keep DB id for an exact period-specific row. Legacy Before-Meal /
      // After-Meal ids must not be shared across multiple period rows.
      id: exact ? (dbItem.id ?? null) : null,
      min: dbItem.minimum ?? dbItem.min ?? defaultItem.min,
      max: dbItem.maximum ?? dbItem.max ?? defaultItem.max,
    };
  });
};

const buildBgTargetsBaseline = (defaults, dbRows) => {
  const merged = mergeBgTargetsFromDb(defaults, dbRows);
  return merged.map((t) => ({
    id: t.id,
    name: t.label,
    minimum: normalizeNumericValue(t.min, 0),
    maximum: normalizeNumericValue(t.max, 0),
  }));
};

const PanelIntro = ({ title, description }) => (
  <div className="sts-panel-intro">
    <div>
      <h2 className="sts-panel-title">{title}</h2>
      {description && <p className="sts-panel-desc">{description}</p>}
    </div>
  </div>
);

const DAILY_CARD_ICONS = [
  // Wake-up — sun
  (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="5" />
      <line x1="12" y1="19" x2="12" y2="22" />
      <line x1="2" y1="12" x2="5" y2="12" />
      <line x1="19" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="4.93" x2="7.05" y2="7.05" />
      <line x1="16.95" y1="16.95" x2="19.07" y2="19.07" />
      <line x1="4.93" y1="19.07" x2="7.05" y2="16.95" />
      <line x1="16.95" y1="7.05" x2="19.07" y2="4.93" />
    </svg>
  ),
  // Breakfast — coffee mug
  (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M17 8h1.5a3.5 3.5 0 010 7H17" />
      <path d="M4 8h13v8a3 3 0 01-3 3H7a3 3 0 01-3-3V8z" />
      <line x1="7" y1="3" x2="7" y2="6" />
      <line x1="11" y1="3" x2="11" y2="6" />
      <line x1="15" y1="3" x2="15" y2="6" />
    </svg>
  ),
  // Lunch — plate with fork & knife
  (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="13" r="6" />
      <line x1="5" y1="3" x2="5" y2="9" />
      <path d="M3.5 3v3a1.5 1.5 0 003 0V3" />
      <line x1="19" y1="3" x2="19" y2="11" />
      <path d="M17 3c0 2 2 2.5 2 5" />
    </svg>
  ),
  // Dinner — utensils
  (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 2v7a2 2 0 002 2h0a2 2 0 002-2V2" />
      <line x1="9" y1="11" x2="9" y2="22" />
      <line x1="5" y1="2" x2="5" y2="7" />
      <line x1="13" y1="2" x2="13" y2="7" />
      <path d="M17 2v8h2a2 2 0 012 2v10" />
      <line x1="17" y1="12" x2="17" y2="22" />
    </svg>
  ),
  // Bed — bed
  (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2 12h20v6a1 1 0 01-1 1H3a1 1 0 01-1-1v-6z" />
      <path d="M2 12V7a2 2 0 012-2h7a2 2 0 012 2v5" />
      <line x1="2" y1="19" x2="2" y2="21" />
      <line x1="22" y1="19" x2="22" y2="21" />
      <line x1="2" y1="15" x2="22" y2="15" />
    </svg>
  ),
];

const SUBNAV_ITEMS = [
  { id: "daily", label: "Daily Schedule" },
  { id: "bp", label: "Blood Pressure" },
  { id: "bg", label: "Blood Glucose" },
  { id: "weight", label: "Weight" },
];

const ScheduleTargetSettings = forwardRef(function ScheduleTargetSettings({
  practiceId,
  patientId,
  isReadOnly = false,
  readOnlyMessage = 'This practice is locked. Schedule and targets are view-only; saves are disabled.',
  showSectionEventLogButtons = true,
  onOpenEventLog = null,
  isParentTabActive = true,
  onSaved = null,
  /** Patient or practice monitoring enrollment (e.g. BP / BG / Weight). Unset sections stay disabled. */
  patientVitals = null,
  enabledMonitoring = null,
  /** Practice-level BP / BG / Weight enrollment. Missing vitals stay view-only on the patient. */
  practiceEnabledMonitoring = null,
  /** Enrollment collects edits and saves them with the patient. Do not write practice or patient rows from this screen. */
  draftMode = false,
}, ref) {
  // Show loading spinner until the initial DB fetch has populated values.
  // We only flip to the real form once `initialData` is set (i.e. fetch finished).
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [initialData, setInitialData] = useState(null);
  const isInitialLoading = loading && initialData === null;
  const navigate = useNavigate();
  const location = useLocation();
  const isAnySaving = Object.values(saving || {}).some(Boolean);
  const [activePanel, setActivePanel] = useState("daily");
  const prevParentTabActiveRef = useRef(isParentTabActive);
  const savedSnapshotRef = useRef(null);
  const [applyPatientsModal, setApplyPatientsModal] = useState({
    open: false,
    patients: [],
    sections: null,
    sectionLabel: "settings",
    dataToSave: null,
    saveKey: null,
    successMessage: ""
  });
  const [applyingToPatients, setApplyingToPatients] = useState(false);

  // Restore unsaved changes for a specific section or all sections back to the last saved snapshot
  const restoreFromBaseline = useCallback((section = null) => {
    const snapshot = savedSnapshotRef.current;
    if (!snapshot) return;

    if (!section || section === "daily") {
      if (Array.isArray(snapshot.dailySchedule)) {
        setDailySchedule(JSON.parse(JSON.stringify(snapshot.dailySchedule)));
      }
    }
    if (!section || section === "bp") {
      if (Array.isArray(snapshot.bpPeriods)) {
        setBpPeriods(JSON.parse(JSON.stringify(snapshot.bpPeriods)));
      }
      if (Array.isArray(snapshot.bpTargets)) {
        setBpTargets(JSON.parse(JSON.stringify(snapshot.bpTargets)));
      }
    }
    if (!section || section === "bg") {
      if (Array.isArray(snapshot.bgPeriods)) {
        setBgPeriods(JSON.parse(JSON.stringify(snapshot.bgPeriods)));
      }
      if (Array.isArray(snapshot.bgTargets)) {
        setBgTargets(JSON.parse(JSON.stringify(snapshot.bgTargets)));
      }
    }
    if (!section || section === "weight") {
      if (Array.isArray(snapshot.weightPeriods)) {
        setWeightPeriods(JSON.parse(JSON.stringify(snapshot.weightPeriods)));
      }
      if (snapshot.weightTargets && typeof snapshot.weightTargets === "object") {
        setWeightTargets(JSON.parse(JSON.stringify(snapshot.weightTargets)));
      }
    }
  }, []);

  const handleTabChange = useCallback((newPanelId) => {
    if (newPanelId === activePanel) return;
    restoreFromBaseline();
    setActivePanel(newPanelId);
  }, [activePanel, restoreFromBaseline]);

  const sectionEnabled = useMemo(() => {
    if (enabledMonitoring && typeof enabledMonitoring === 'object') {
      return {
        daily: true,
        bp: Boolean(enabledMonitoring.bp ?? enabledMonitoring.bloodPressure),
        bg: Boolean(enabledMonitoring.bg ?? enabledMonitoring.bloodGlucose),
        weight: Boolean(enabledMonitoring.weight ?? enabledMonitoring.wt)
      };
    }
    if (!patientId) {
      return { daily: true, bp: true, bg: true, weight: true };
    }
    const parsed = parsePatientVitals(patientVitals);
    return {
      daily: true,
      bp: parsed.bp,
      bg: parsed.bg,
      weight: parsed.wt
    };
  }, [patientId, patientVitals, enabledMonitoring]);

  const isSectionLocked = useCallback((sectionId) => {
    if (isReadOnly) return true;
    if (sectionId === 'daily') return false;
    if (!practiceEnabledMonitoring || typeof practiceEnabledMonitoring !== 'object') return false;
    const flags = {
      bp: Boolean(practiceEnabledMonitoring.bp ?? practiceEnabledMonitoring.bloodPressure ?? true),
      bg: Boolean(practiceEnabledMonitoring.bg ?? practiceEnabledMonitoring.bloodGlucose ?? true),
      weight: Boolean(practiceEnabledMonitoring.weight ?? practiceEnabledMonitoring.wt ?? true),
    };
    return flags[sectionId] === false;
  }, [isReadOnly, practiceEnabledMonitoring]);

  const bpLocked = isSectionLocked('bp');
  const bgLocked = isSectionLocked('bg');
  const weightLocked = isSectionLocked('weight');

  useEffect(() => {
    if (isParentTabActive && !prevParentTabActiveRef.current) {
      restoreFromBaseline();
      setActivePanel("daily");
    }
    prevParentTabActiveRef.current = isParentTabActive;
  }, [isParentTabActive, restoreFromBaseline]);

  useEffect(() => {
    if (!sectionEnabled[activePanel]) {
      restoreFromBaseline();
      setActivePanel('daily');
    }
  }, [sectionEnabled, activePanel, restoreFromBaseline]);

  const handleEventLog = (section = null) => {
    if (typeof onOpenEventLog === 'function') {
      onOpenEventLog(section);
      return;
    }
    if (isAnySaving) return;
    if (!practiceId) return;
    // Practice schedule logs use moduleId='Schedule' with targetUserId='all' (or practice id).
    // Patient schedule logs use moduleId='Patient Schedule' with the patients table id as the route target
    // (EventLogSection / PatientsLogContent resolve via patientId / target user).
    if (patientId) {
      let path = `/event-logs/Patient Schedule/${patientId}?practiceId=${practiceId}`;
      if (section) {
        path += `&section=${section}`;
      }
      navigate(appendFromDashboardQuery(path, location));
      return;
    }
    let path = `/event-logs/Schedule/all?practiceId=${practiceId}`;
    if (section) {
      path += `&section=${section}`;
    }
    navigate(appendFromDashboardQuery(path, location));
  };

  const getActiveSectionForEventLog = useCallback(() => {
    switch (activePanel) {
      case "daily":
        return "dailySchedule";
      case "bp":
        return "bloodPressure";
      case "bg":
        return "bloodGlucose";
      case "weight":
        return "weight";
      default:
        return null;
    }
  }, [activePanel]);


  // Daily Schedule - using 12-hour format
  const [dailySchedule, setDailySchedule] = useState([
    { id: 1, name: "Wake-up Time", time: "06:00 AM" },
    { id: 2, name: "Breakfast Time", time: "08:00 AM" },
    { id: 3, name: "Lunch Time", time: "12:00 PM" },
    { id: 4, name: "Dinner Time", time: "06:00 PM" },
    { id: 5, name: "Bed Time", time: "10:00 PM" },
  ]);

  // Permanent BP period start times — these are fixed and cannot be edited
  const BP_PERMANENT_START_TIMES = {
    'Wake-up': '05:00 AM',
    'Morning': '08:00 AM',
    'Noon': '11:00 AM',
    'Afternoon': '02:00 PM',
    'Evening': '05:00 PM',
  };

  // Blood Pressure Periods & Targets - Fixed start times (matching image)
  const [bpPeriods, setBpPeriods] = useState([
    { id: 1, name: "Wake-up", start: "05:00 AM", end: "07:59 AM", fixed: true },
    { id: 2, name: "Morning", start: "08:00 AM", end: "10:59 AM", fixed: true },
    { id: 3, name: "Noon", start: "11:00 AM", end: "01:59 PM", fixed: true },
    { id: 4, name: "Afternoon", start: "02:00 PM", end: "04:59 PM", fixed: true },
    { id: 5, name: "Evening", start: "05:00 PM", end: "04:59 AM", fixed: true },
  ]);

  const [bpTargets, setBpTargets] = useState([
    { id: 1, label: "Wake-up Target Range", sysMin: 100, sysMax: 140, diaMin: 60, diaMax: 90, pulseMin: 60, pulseMax: 100 },
    { id: 2, label: "Morning Target Range", sysMin: 100, sysMax: 140, diaMin: 60, diaMax: 90, pulseMin: 60, pulseMax: 100 },
    { id: 3, label: "Noon Target Range", sysMin: 100, sysMax: 140, diaMin: 60, diaMax: 90, pulseMin: 60, pulseMax: 100 },
    { id: 4, label: "Afternoon Target Range", sysMin: 100, sysMax: 140, diaMin: 60, diaMax: 90, pulseMin: 60, pulseMax: 100 },
    { id: 5, label: "Evening Target Range", sysMin: 100, sysMax: 140, diaMin: 60, diaMax: 90, pulseMin: 60, pulseMax: 100 },
  ]);

  // Permanent BG period start times — these are fixed and cannot be edited
  const BG_PERMANENT_START_TIMES = {
    'Wake-up Time': '06:00 AM',
    'Before-Breakfast': '07:00 AM',
    'After-Breakfast': '08:00 AM',
    'Before-Lunch': '11:00 AM',
    'After-Lunch': '01:00 PM',
    'Before-Dinner': '05:00 PM',
    'After-Dinner': '07:00 PM',
    'Bedtime': '08:30 PM',
    'Midnight': '10:00 PM',
  };

  // Blood Glucose Periods & Targets - Fixed start times
  const [bgPeriods, setBgPeriods] = useState([
    { id: 1, name: "Wake-up Time", start: "06:00 AM", end: "06:59 AM", fixed: true },
    { id: 2, name: "Before-Breakfast", start: "07:00 AM", end: "07:59 AM", fixed: true },
    { id: 3, name: "After-Breakfast", start: "08:00 AM", end: "10:59 AM", fixed: true },
    { id: 4, name: "Before-Lunch", start: "11:00 AM", end: "12:59 PM", fixed: true },
    { id: 5, name: "After-Lunch", start: "01:00 PM", end: "04:59 PM", fixed: true },
    { id: 6, name: "Before-Dinner", start: "05:00 PM", end: "06:59 PM", fixed: true },
    { id: 7, name: "After-Dinner", start: "07:00 PM", end: "08:29 PM", fixed: true },
    { id: 8, name: "Bedtime", start: "08:30 PM", end: "09:59 PM", fixed: true },
    { id: 9, name: "Midnight", start: "10:00 PM", end: "05:59 AM", fixed: true },
  ]);

  const [bgTargets, setBgTargets] = useState(() =>
    DEFAULT_BG_TARGETS.map((t) => ({ ...t }))
  );

  // Permanent Weight period start times — these are fixed and cannot be edited
  const WEIGHT_PERMANENT_START_TIMES = {
    'Morning': '06:00 AM',
    'Afternoon': '12:00 PM',
    'Evening': '06:00 PM',
  };

  // Weight & Fat Targets
  const [weightTargets, setWeightTargets] = useState({
    weightMin: 66,
    weightMax: 220,
    bodyFatMin: 14,
    bodyFatMax: 27,
    bmiUnderweight: "",
    bmiNormal: 18.5,
    bmiOverweight: 25,
    bmiObese: 30,
  });

  const [weightPeriods, setWeightPeriods] = useState([
    { id: 1, name: "Morning", start: "06:00 AM", end: "11:59 AM", fixed: true },
    { id: 2, name: "Afternoon", start: "12:00 PM", end: "05:59 PM", fixed: true },
    { id: 3, name: "Evening", start: "06:00 PM", end: "05:59 AM", fixed: true },
  ]);

  // Fetch data from backend
  useEffect(() => {
    if (!practiceId) return;

    const fetchScheduleTargets = async () => {
      try {
        setLoading(true);
        let response;
        if (patientId) {
          response = await patientService.getScheduleAndTargets(practiceId, patientId);
        } else {
          response = await practiceService.getScheduleAndTargets(practiceId);
        }
        if (response.data?.success && response.data?.data) {
          const data = response.data.data;

          // IMPORTANT: merge DB rows on top of the React-default rows (matching
          // by name). Replacing entirely with whatever the DB returned makes
          // sections "disappear" on staging: if a brand-new patient has saved
          // only Bed Time (so DB has 1 row), `setDailySchedule(dbRows)` would
          // collapse the visible list from 5 rows to 1. By keeping the fixed
          // default list as the structural skeleton and overlaying DB values,
          // the user always sees the full section and partial DB data is
          // simply reflected on the rows that have it.
          let resolvedDailySchedule = dailySchedule;
          if (data.dailySchedule && data.dailySchedule.length > 0) {
            const dbByName = mapByName(data.dailySchedule);
            resolvedDailySchedule = dailySchedule.map((defaultItem) => {
              const dbItem = dbByName.get(normalizeName(defaultItem.name));
              if (!dbItem) return defaultItem;
              return {
                ...defaultItem,
                id: dbItem.id ?? defaultItem.id,
                time: dbItem.time ? convertTimeTo12Hour(dbItem.time) : defaultItem.time
              };
            });
            setDailySchedule(resolvedDailySchedule);
          }

          let resolvedBpPeriods = bpPeriods;
          if (data.bloodPressureTimePeriod && data.bloodPressureTimePeriod.length > 0) {
            const dbByName = mapByName(data.bloodPressureTimePeriod);
            resolvedBpPeriods = bpPeriods.map((defaultItem) => {
              const dbItem = dbByName.get(normalizeName(defaultItem.name));
              if (!dbItem) return defaultItem;
              return {
                ...defaultItem,
                id: dbItem.id ?? defaultItem.id,
                start: BP_PERMANENT_START_TIMES[defaultItem.name] || (dbItem.startTime ? convertTimeTo12Hour(dbItem.startTime) : defaultItem.start),
                end: dbItem.endTime ? convertTimeTo12Hour(dbItem.endTime) : defaultItem.end,
                fixed: true
              };
            });
            setBpPeriods(resolvedBpPeriods);
          }

          let resolvedBgPeriods = bgPeriods;
          if (data.bloodGlucoseTimePeriod && data.bloodGlucoseTimePeriod.length > 0) {
            const dbByName = mapByName(data.bloodGlucoseTimePeriod);
            resolvedBgPeriods = bgPeriods.map((defaultItem) => {
              const dbItem = dbByName.get(normalizeName(defaultItem.name));
              if (!dbItem) return defaultItem;
              return {
                ...defaultItem,
                id: dbItem.id ?? defaultItem.id,
                start: BG_PERMANENT_START_TIMES[defaultItem.name] || (dbItem.startTime ? convertTimeTo12Hour(dbItem.startTime) : defaultItem.start),
                end: dbItem.endTime ? convertTimeTo12Hour(dbItem.endTime) : defaultItem.end,
                fixed: true
              };
            });
            setBgPeriods(resolvedBgPeriods);
          }

          let resolvedBgTargets = bgTargets;
          if (data.bloodGlucoseTargetRange && data.bloodGlucoseTargetRange.length > 0) {
            resolvedBgTargets = mergeBgTargetsFromDb(bgTargets, data.bloodGlucoseTargetRange);
            setBgTargets(resolvedBgTargets);
          }

          let resolvedWeightPeriods = weightPeriods;
          if (data.weightTimePeriod && data.weightTimePeriod.length > 0) {
            const dbByName = mapByName(data.weightTimePeriod);
            resolvedWeightPeriods = weightPeriods.map((defaultItem) => {
              const dbItem = dbByName.get(normalizeName(defaultItem.name));
              if (!dbItem) return defaultItem;
              return {
                ...defaultItem,
                id: dbItem.id ?? defaultItem.id,
                start: WEIGHT_PERMANENT_START_TIMES[defaultItem.name] || (dbItem.startTime ? convertTimeTo12Hour(dbItem.startTime) : defaultItem.start),
                end: dbItem.endTime ? convertTimeTo12Hour(dbItem.endTime) : defaultItem.end,
                fixed: true
              };
            });
            setWeightPeriods(resolvedWeightPeriods);
          }

          let resolvedWeightTargets = weightTargets;
          if (data.weightTargetRange && Object.keys(data.weightTargetRange).length > 0) {
            resolvedWeightTargets = {
              ...weightTargets,
              weightMin: normalizeNumericValue(data.weightTargetRange.weightMin, weightTargets.weightMin),
              weightMax: normalizeNumericValue(data.weightTargetRange.weightMax, weightTargets.weightMax),
              bodyFatMin: normalizeNumericValue(data.weightTargetRange.bodyFatMin, weightTargets.bodyFatMin),
              bodyFatMax: normalizeNumericValue(data.weightTargetRange.bodyFatMax, weightTargets.bodyFatMax),
              bmiUnderweight: data.weightTargetRange.bmiUnderweight ?? weightTargets.bmiUnderweight,
              bmiNormal: 18.5,
              bmiOverweight: 25,
              bmiObese: 30
            };
            setWeightTargets(resolvedWeightTargets);
          }

          let resolvedBpTargets = bpTargets;
          if (data.bloodPressureTargetRange && data.bloodPressureTargetRange.length > 0) {
            const dbByName = mapByName(data.bloodPressureTargetRange);
            resolvedBpTargets = bpTargets.map((defaultItem) => {
              const dbItem = dbByName.get(normalizeName(defaultItem.label));
              if (!dbItem) return defaultItem;
              return {
                ...defaultItem,
                id: dbItem.id ?? defaultItem.id,
                sysMin: dbItem.systolicMin ?? dbItem.sysMin ?? defaultItem.sysMin,
                sysMax: dbItem.systolicMax ?? dbItem.sysMax ?? defaultItem.sysMax,
                diaMin: dbItem.diastolicMin ?? dbItem.diaMin ?? defaultItem.diaMin,
                diaMax: dbItem.diastolicMax ?? dbItem.diaMax ?? defaultItem.diaMax,
                pulseMin: dbItem.pulseMin ?? dbItem.pulse_min ?? defaultItem.pulseMin,
                pulseMax: dbItem.pulseMax ?? dbItem.pulse_max ?? defaultItem.pulseMax
              };
            });
            setBpTargets(resolvedBpTargets);
          }

          // Save a snapshot of the clean React state values so tabs can be reset if left unsaved
          savedSnapshotRef.current = {
            dailySchedule: JSON.parse(JSON.stringify(resolvedDailySchedule)),
            bpPeriods: JSON.parse(JSON.stringify(resolvedBpPeriods)),
            bpTargets: JSON.parse(JSON.stringify(resolvedBpTargets)),
            bgPeriods: JSON.parse(JSON.stringify(resolvedBgPeriods)),
            bgTargets: JSON.parse(JSON.stringify(resolvedBgTargets)),
            weightPeriods: JSON.parse(JSON.stringify(resolvedWeightPeriods)),
            weightTargets: JSON.parse(JSON.stringify(resolvedWeightTargets))
          };


          // Build the dirty-check baselines. They MUST mirror the merged React
          // state (full default list with DB rows overlaid by name) — never
          // just the raw DB array. Otherwise:
          //   - If DB returned 1 row, baseline has 1 entry, the other 4 rows
          //     in React state have no match in baseline, and the dirty check
          //     flags them as "changed" on the next save (logging fields the
          //     user never touched).
          //   - If DB returned nothing, fall back to defaults so first-time
          //     saves don't mass-flag every row.
          const buildMergedBaseline = (defaults, dbRows, mapDefaultToBaseline, mapDbToBaseline) => {
            const dbByName = mapByName(dbRows);
            return defaults.map((d) => {
              const dbItem = dbByName.get(normalizeName(d?.name ?? d?.label));
              return dbItem ? mapDbToBaseline(d, dbItem) : mapDefaultToBaseline(d);
            });
          };

          const baselineDaily = buildMergedBaseline(
            dailySchedule,
            data.dailySchedule,
            (s) => ({ id: s.id, name: s.name, time: convertTimeTo24Hour(s.time) }),
            (s, db) => ({ id: db.id ?? s.id, name: db.name ?? s.name, time: db.time })
          );

          const baselineBpPeriods = buildMergedBaseline(
            bpPeriods,
            data.bloodPressureTimePeriod,
            (p) => ({
              id: p.id, name: p.name,
              startTime: convertTimeTo24Hour(p.start),
              endTime: convertTimeTo24Hour(p.end)
            }),
            (p, db) => ({
              id: db.id ?? p.id,
              name: db.name ?? p.name,
              startTime: db.startTime || convertTimeTo24Hour(p.start),
              endTime: db.endTime || convertTimeTo24Hour(p.end)
            })
          );

          const baselineBpTargets = buildMergedBaseline(
            bpTargets,
            data.bloodPressureTargetRange,
            (t) => ({
              id: t.id, name: t.label,
              systolicMin: normalizeNumericValue(t.sysMin, 0),
              systolicMax: normalizeNumericValue(t.sysMax, 0),
              diastolicMin: normalizeNumericValue(t.diaMin, 0),
              diastolicMax: normalizeNumericValue(t.diaMax, 0),
              pulseMin: normalizeNumericValue(t.pulseMin, 60),
              pulseMax: normalizeNumericValue(t.pulseMax, 100)
            }),
            (t, db) => ({
              id: db.id ?? t.id,
              name: db.name ?? t.label,
              systolicMin: normalizeNumericValue(db.systolicMin, normalizeNumericValue(t.sysMin, 0)),
              systolicMax: normalizeNumericValue(db.systolicMax, normalizeNumericValue(t.sysMax, 0)),
              diastolicMin: normalizeNumericValue(db.diastolicMin, normalizeNumericValue(t.diaMin, 0)),
              diastolicMax: normalizeNumericValue(db.diastolicMax, normalizeNumericValue(t.diaMax, 0)),
              pulseMin: normalizeNumericValue(db.pulseMin, normalizeNumericValue(t.pulseMin, 60)),
              pulseMax: normalizeNumericValue(db.pulseMax, normalizeNumericValue(t.pulseMax, 100))
            })
          );

          const baselineBgPeriods = buildMergedBaseline(
            bgPeriods,
            data.bloodGlucoseTimePeriod,
            (p) => ({
              id: p.id, name: p.name,
              startTime: convertTimeTo24Hour(p.start),
              endTime: convertTimeTo24Hour(p.end)
            }),
            (p, db) => ({
              id: db.id ?? p.id,
              name: db.name ?? p.name,
              startTime: db.startTime || convertTimeTo24Hour(p.start),
              endTime: db.endTime || convertTimeTo24Hour(p.end)
            })
          );

          const baselineBgTargets = buildBgTargetsBaseline(
            bgTargets,
            data.bloodGlucoseTargetRange
          );

          const baselineWeightPeriods = buildMergedBaseline(
            weightPeriods,
            data.weightTimePeriod,
            (p) => ({
              id: p.id, name: p.name,
              startTime: convertTimeTo24Hour(p.start),
              endTime: convertTimeTo24Hour(p.end)
            }),
            (p, db) => ({
              id: db.id ?? p.id,
              name: db.name ?? p.name,
              startTime: db.startTime || convertTimeTo24Hour(p.start),
              endTime: db.endTime || convertTimeTo24Hour(p.end)
            })
          );

          const baselineWeightTargetRange = (data.weightTargetRange && Object.keys(data.weightTargetRange).length > 0)
            ? JSON.parse(JSON.stringify(data.weightTargetRange))
            : {
                weightMin: normalizeNumericValue(weightTargets.weightMin, 66),
                weightMax: normalizeNumericValue(weightTargets.weightMax, 220),
                bodyFatMin: normalizeNumericValue(weightTargets.bodyFatMin, 14),
                bodyFatMax: normalizeNumericValue(weightTargets.bodyFatMax, 27),
                bmiUnderweight: weightTargets.bmiUnderweight || "",
                bmiNormal: 18.5,
                bmiOverweight: 25,
                bmiObese: 30
              };

          setInitialData({
            dailySchedule: baselineDaily,
            bpPeriods: baselineBpPeriods,
            bpTargets: baselineBpTargets,
            bgPeriods: baselineBgPeriods,
            bgTargets: baselineBgTargets,
            weightPeriods: baselineWeightPeriods,
            weightTargetRange: baselineWeightTargetRange
          });

        }
      } catch (error) {
        console.error("Error fetching schedule targets:", error);
        toast.error("Failed to load schedule targets");
        // Mark initialData with a non-null empty shape so the loading UI clears
        // and the user can at least see the defaults if the fetch crashed.
        setInitialData((prev) => prev ?? {
          dailySchedule: [], bpPeriods: [], bpTargets: [], bgPeriods: [],
          bgTargets: [], weightPeriods: [], weightTargetRange: {}
        });
      } finally {
        setLoading(false);
      }
    };

    fetchScheduleTargets();
  }, [practiceId, patientId]);

  // Handlers
  const updateDaily = (i, val) => {
    const copy = [...dailySchedule];
    copy[i].time = val;
    setDailySchedule(copy);
  };

  const updateBpPeriod = (i, field, val) => {
    const copy = [...bpPeriods];
    // Don't allow editing start time (it's fixed)
    if (field === 'start') return;
    copy[i][field] = val;
    setBpPeriods(copy);
  };

  const updateBpTarget = (i, field, val) => {
    const copy = [...bpTargets];
    // Allow empty string for blank input, only parse if not empty
    copy[i][field] = val === "" ? "" : (parseInt(val) || 0);
    setBpTargets(copy);
  };

  const updateBgPeriod = (i, field, val) => {
    const copy = [...bgPeriods];
    if (field === 'start') return; // Don't allow editing start time
    copy[i][field] = val;
    setBgPeriods(copy);
  };

  const updateBgTarget = (i, field, val) => {
    setBgTargets((prev) =>
      prev.map((target, idx) =>
        idx === i
          ? { ...target, [field]: val === "" ? "" : (parseInt(val, 10) || 0) }
          : target
      )
    );
  };

  const updateWeightTarget = (field, val) => {
    // Prevent changes to permanent BMI fields
    if (['bmiUnderweight', 'bmiNormal', 'bmiOverweight', 'bmiObese'].includes(field)) return;
    
    setWeightTargets(prev => ({
      ...prev,
      [field]: val === "" ? "" : normalizeNumericValue(val, 0)
    }));
  };

  const updateWeightPeriod = (i, field, val) => {
    const copy = [...weightPeriods];
    if (field === 'start') return; // Don't allow editing start time
    copy[i][field] = val;
    setWeightPeriods(copy);
  };

  // Build minimal payloads (changed rows only) for faster saves.
  //
  // We attach `_previous` to each changed row carrying the value that was on
  // screen before the edit (pulled from initialData, i.e. the last fetched/
  // reloaded baseline). The backend uses this as a fallback for the audit log
  // when the DB has no prior row for that item — e.g. brand-new patients on
  // staging where the section is being persisted for the first time. Without
  // it, "Old Data" in the event log would show "—".
  const getChangedDailySchedulePayload = () => {
    if (!initialData) return [];
    const initialMap = new Map(
      (initialData.dailySchedule || []).map((s) => [String(s.id), convertTimeTo24Hour(s.time)])
    );
    const initialByName = new Map(
      (initialData.dailySchedule || []).map((s) => [normalizeName(s.name), convertTimeTo24Hour(s.time)])
    );
    return dailySchedule
      .map((item) => {
        const time = convertTimeTo24Hour(item.time);
        const prevTime = initialMap.get(String(item.id)) ?? initialByName.get(normalizeName(item.name));
        return {
          id: item.id,
          name: item.name,
          time,
          _previous: { time: prevTime ?? null }
        };
      })
      .filter((item) => (item._previous?.time ?? null) !== item.time);
  };

  const getChangedBpPeriodsPayload = () => {
    if (!initialData) return [];
    const initialMap = new Map(
      (initialData.bpPeriods || []).map((p) => [
        String(p.id),
        { name: p.name, endTime: convertTimeTo24Hour(p.endTime || p.end) }
      ])
    );
    const initialByName = new Map(
      (initialData.bpPeriods || []).map((p) => [normalizeName(p.name), convertTimeTo24Hour(p.endTime || p.end)])
    );
    return bpPeriods
      .map((p) => {
        const endTime = convertTimeTo24Hour(p.end);
        const prevEnd = initialMap.get(String(p.id))?.endTime ?? initialByName.get(normalizeName(p.name));
        return {
          id: p.id,
          name: p.name,
          startTime: convertTimeTo24Hour(p.start),
          endTime,
          _previous: { endTime: prevEnd ?? null }
        };
      })
      .filter((p) => (p._previous?.endTime ?? null) !== p.endTime);
  };

  const getChangedBpTargetsPayload = () => {
    if (!initialData) return [];
    const buildInitial = (t) => ({
      systolicMin: normalizeNumericValue(t.systolicMin, 0),
      systolicMax: normalizeNumericValue(t.systolicMax, 0),
      diastolicMin: normalizeNumericValue(t.diastolicMin, 0),
      diastolicMax: normalizeNumericValue(t.diastolicMax, 0),
      pulseMin: normalizeNumericValue(t.pulseMin, 60),
      pulseMax: normalizeNumericValue(t.pulseMax, 100)
    });
    const initialMap = new Map(
      (initialData.bpTargets || []).map((t) => [String(t.id), buildInitial(t)])
    );
    const initialByName = new Map(
      (initialData.bpTargets || []).map((t) => [normalizeName(t.name), buildInitial(t)])
    );
    return bpTargets
      .map((t) => {
        const current = {
          systolicMin: normalizeNumericValue(t.sysMin, 0),
          systolicMax: normalizeNumericValue(t.sysMax, 0),
          diastolicMin: normalizeNumericValue(t.diaMin, 0),
          diastolicMax: normalizeNumericValue(t.diaMax, 0),
          pulseMin: normalizeNumericValue(t.pulseMin, 60),
          pulseMax: normalizeNumericValue(t.pulseMax, 100)
        };
        const prev = initialMap.get(String(t.id)) ?? initialByName.get(normalizeName(t.label)) ?? null;
        return {
          id: t.id,
          name: t.label,
          ...current,
          _previous: prev
        };
      })
      .filter((t) => {
        const prev = t._previous;
        if (!prev) return true;
        return (
          prev.systolicMin !== t.systolicMin ||
          prev.systolicMax !== t.systolicMax ||
          prev.diastolicMin !== t.diastolicMin ||
          prev.diastolicMax !== t.diastolicMax ||
          prev.pulseMin !== t.pulseMin ||
          prev.pulseMax !== t.pulseMax
        );
      });
  };

  const getChangedBgPeriodsPayload = () => {
    if (!initialData) return [];
    const initialMap = new Map(
      (initialData.bgPeriods || []).map((p) => [
        String(p.id),
        { name: p.name, endTime: convertTimeTo24Hour(p.endTime || p.end) }
      ])
    );
    const initialByName = new Map(
      (initialData.bgPeriods || []).map((p) => [normalizeName(p.name), convertTimeTo24Hour(p.endTime || p.end)])
    );
    return bgPeriods
      .map((p) => {
        const endTime = convertTimeTo24Hour(p.end);
        const prevEnd = initialMap.get(String(p.id))?.endTime ?? initialByName.get(normalizeName(p.name));
        return {
          id: p.id,
          name: p.name,
          startTime: convertTimeTo24Hour(p.start),
          endTime,
          _previous: { endTime: prevEnd ?? null }
        };
      })
      .filter((p) => (p._previous?.endTime ?? null) !== p.endTime);
  };

  const getChangedBgTargetsPayload = () => {
    if (!initialData) return [];
    const buildInitial = (t) => ({
      minimum: normalizeNumericValue(t.minimum, 0),
      maximum: normalizeNumericValue(t.maximum, 0)
    });
    const initialMap = new Map(
      (initialData.bgTargets || [])
        .filter((t) => t?.id != null)
        .map((t) => [String(t.id), buildInitial(t)])
    );
    const initialByName = new Map(
      (initialData.bgTargets || []).map((t) => [normalizeName(t.name), buildInitial(t)])
    );
    return bgTargets
      .map((t) => {
        const current = {
          minimum: normalizeNumericValue(t.min, 0),
          maximum: normalizeNumericValue(t.max, 0)
        };
        const prev =
          (t.id != null ? initialMap.get(String(t.id)) : null) ??
          initialByName.get(normalizeName(t.label)) ??
          null;
        return {
          id: t.id,
          name: t.label,
          ...current,
          _previous: prev
        };
      })
      .filter((t) => {
        const prev = t._previous;
        if (!prev) return true;
        return (
          prev.minimum !== t.minimum ||
          prev.maximum !== t.maximum
        );
      });
  };

  const getChangedWeightPeriodsPayload = () => {
    if (!initialData) return [];
    const initialMap = new Map(
      (initialData.weightPeriods || []).map((p) => [
        String(p.id),
        { name: p.name, endTime: convertTimeTo24Hour(p.endTime || p.end) }
      ])
    );
    const initialByName = new Map(
      (initialData.weightPeriods || []).map((p) => [normalizeName(p.name), convertTimeTo24Hour(p.endTime || p.end)])
    );
    return weightPeriods
      .map((p) => {
        const endTime = convertTimeTo24Hour(p.end);
        const prevEnd = initialMap.get(String(p.id))?.endTime ?? initialByName.get(normalizeName(p.name));
        return {
          id: p.id,
          name: p.name,
          startTime: convertTimeTo24Hour(p.start),
          endTime,
          _previous: { endTime: prevEnd ?? null }
        };
      })
      .filter((p) => (p._previous?.endTime ?? null) !== p.endTime);
  };

  const getChangedWeightTargetsPayload = () => {
    if (!initialData) return null;
    const current = {
      weightMin: normalizeNumericValue(weightTargets.weightMin, 66),
      weightMax: normalizeNumericValue(weightTargets.weightMax, 220),
      bodyFatMin: normalizeNumericValue(weightTargets.bodyFatMin, 14),
      bodyFatMax: normalizeNumericValue(weightTargets.bodyFatMax, 27),
      bmiUnderweight: weightTargets.bmiUnderweight || "",
      bmiNormal: 18.5,
      bmiOverweight: 25,
      bmiObese: 30
    };
    const initial = {
      weightMin: normalizeNumericValue(initialData.weightTargetRange?.weightMin, 66),
      weightMax: normalizeNumericValue(initialData.weightTargetRange?.weightMax, 220),
      bodyFatMin: normalizeNumericValue(initialData.weightTargetRange?.bodyFatMin, 14),
      bodyFatMax: normalizeNumericValue(initialData.weightTargetRange?.bodyFatMax, 27),
      bmiUnderweight: initialData.weightTargetRange?.bmiUnderweight || "",
      bmiNormal: 18.5,
      bmiOverweight: 25,
      bmiObese: 30
    };
    if (deepEqual(current, initial)) return null;
    // Tag the previous (UI-displayed) values so the audit log can fall back to
    // them when the DB has no prior weight_target row for this user.
    return { ...current, _previous: initial };
  };

  useImperativeHandle(ref, () => ({
    isReady: () => initialData != null && !loading,
    getChangedPayload: () => {
      const payload = {};
      const dailyScheduleChanges = getChangedDailySchedulePayload();
      if (dailyScheduleChanges.length) payload.dailySchedule = dailyScheduleChanges;
      const bloodPressureTimePeriod = getChangedBpPeriodsPayload();
      if (bloodPressureTimePeriod.length) payload.bloodPressureTimePeriod = bloodPressureTimePeriod;
      const bloodPressureTargetRange = getChangedBpTargetsPayload();
      if (bloodPressureTargetRange.length) payload.bloodPressureTargetRange = bloodPressureTargetRange;
      const bloodGlucoseTimePeriod = getChangedBgPeriodsPayload();
      if (bloodGlucoseTimePeriod.length) payload.bloodGlucoseTimePeriod = bloodGlucoseTimePeriod;
      const bloodGlucoseTargetRange = getChangedBgTargetsPayload();
      if (bloodGlucoseTargetRange.length) payload.bloodGlucoseTargetRange = bloodGlucoseTargetRange;
      const weightTimePeriod = getChangedWeightPeriodsPayload();
      if (weightTimePeriod.length) payload.weightTimePeriod = weightTimePeriod;
      const weightTargetRange = getChangedWeightTargetsPayload();
      if (weightTargetRange) payload.weightTargetRange = weightTargetRange;
      return Object.keys(payload).length ? payload : null;
    }
  }), [
    initialData,
    loading,
    dailySchedule,
    bpPeriods,
    bpTargets,
    bgPeriods,
    bgTargets,
    weightPeriods,
    weightTargets
  ]);

  // Save functions
  const getSectionLabelForFlags = (sections = {}) => {
    const labels = [];
    if (sections.dailySchedule) labels.push("daily schedule");
    if (sections.bloodPressureTimePeriod || sections.bloodPressureTargetRange) {
      labels.push("blood pressure settings");
    }
    if (sections.bloodGlucoseTimePeriod || sections.bloodGlucoseTargetRange) {
      labels.push("blood glucose settings");
    }
    if (sections.weightTimePeriod || sections.weightTargetRange) {
      labels.push("weight settings");
    }
    if (!labels.length) return "settings";
    if (labels.length === 1) return labels[0];
    return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
  };

  const resetApplyPatientsModal = () => {
    setApplyPatientsModal({
      open: false,
      patients: [],
      sections: null,
      sectionLabel: "settings",
      dataToSave: null,
      saveKey: null,
      successMessage: ""
    });
  };

  const handleCloseApplyPatientsModal = () => {
    if (applyingToPatients) return;
    resetApplyPatientsModal();
  };

  const persistPracticeSettings = async ({
    saveKey,
    dataToSave,
    sections,
    applyMode = APPLY_OPTIONS.PRACTICE_ONLY,
    matchingPatientIds = [],
    successMessage
  }) => {
    const response = await practiceService.updateScheduleAndTargets(practiceId, dataToSave);
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Save failed");
    }

    let appliedCount = 0;
    if (applyMode === APPLY_OPTIONS.ALL) {
      const applyResponse = await practiceService.applyScheduleAndTargetsToPatients(practiceId, {
        applyMode: "all",
        sections
      });
      if (!applyResponse?.data?.success) {
        throw new Error(applyResponse?.data?.message || "Practice saved, but patient update failed");
      }
      appliedCount = Number(applyResponse?.data?.data?.updated || 0);
    } else if (
      applyMode === APPLY_OPTIONS.MATCHING &&
      Array.isArray(matchingPatientIds) &&
      matchingPatientIds.length > 0
    ) {
      const applyResponse = await practiceService.applyScheduleAndTargetsToPatients(practiceId, {
        patientIds: matchingPatientIds,
        sections
      });
      if (!applyResponse?.data?.success) {
        throw new Error(applyResponse?.data?.message || "Practice saved, but patient update failed");
      }
      appliedCount = Number(applyResponse?.data?.data?.updated || matchingPatientIds.length);
    }

    await reloadFromServer();
    if (typeof onSaved === "function") onSaved(saveKey);

    if (appliedCount > 0) {
      toast.success(
        `${successMessage} Updated ${appliedCount} patient${appliedCount === 1 ? "" : "s"}.`
      );
    } else {
      toast.success(successMessage);
    }
  };

  const beginPracticeSave = async ({
    saveKey,
    dataToSave,
    sections,
    successMessage
  }) => {
    setSaving((prev) => ({ ...prev, [saveKey]: true }));
    try {
      let matchingPatients = [];
      try {
        const matchResponse = await practiceService.getMatchingScheduleTargetPatients(
          practiceId,
          sections
        );
        matchingPatients = matchResponse?.data?.data?.patients || [];
      } catch (matchError) {
        console.warn("Could not load matching patients; opening modal anyway:", matchError);
        matchingPatients = [];
      }

      // Always open the confirmation modal before saving practice settings.
      setApplyPatientsModal({
        open: true,
        patients: Array.isArray(matchingPatients) ? matchingPatients : [],
        sections,
        sectionLabel: getSectionLabelForFlags(sections),
        dataToSave,
        saveKey,
        successMessage
      });
    } catch (error) {
      console.error(`Error preparing ${saveKey} schedule settings:`, error);
      toast.error(
        error.response?.data?.message || error.message || "Failed to prepare schedule settings"
      );
    } finally {
      setSaving((prev) => ({ ...prev, [saveKey]: false }));
    }
  };

  const handleConfirmApplyToPatients = async (applyMode = APPLY_OPTIONS.PRACTICE_ONLY) => {
    const {
      dataToSave,
      sections,
      saveKey,
      successMessage,
      patients
    } = applyPatientsModal;

    if (!practiceId || !dataToSave || !sections || !saveKey) {
      handleCloseApplyPatientsModal();
      return;
    }

    try {
      setApplyingToPatients(true);
      await persistPracticeSettings({
        saveKey,
        dataToSave,
        sections,
        applyMode,
        matchingPatientIds: (patients || []).map((p) => p.id),
        successMessage: successMessage || "Settings saved successfully"
      });
      resetApplyPatientsModal();
    } catch (error) {
      console.error("Error saving schedule targets with patient updates:", error);
      toast.error(
        error.response?.data?.message || error.message || "Failed to save settings"
      );
    } finally {
      setApplyingToPatients(false);
    }
  };

  const saveDailySchedule = async () => {
    if (draftMode) return;
    if (isReadOnly) {
      toast.warning("Practice is locked. Schedule and targets are view-only.");
      return;
    }
    if (!practiceId) {
      toast.error("Practice ID is required");
      return;
    }

    const changedDaily = getChangedDailySchedulePayload();
    if (changedDaily.length === 0) {
      toast.info("No daily schedule changes to save");
      return;
    }
    const dataToSave = { dailySchedule: changedDaily };

    if (patientId) {
      try {
        setSaving((prev) => ({ ...prev, daily: true }));
        const response = await patientService.updateScheduleAndTargets(
          practiceId,
          patientId,
          dataToSave
        );
        if (response.data?.success) {
          toast.success("Daily schedule saved successfully");
          await reloadFromServer();
          setUsePatientScheduleTargetsPreference(practiceId, patientId, true);
          if (typeof onSaved === "function") onSaved("daily");
        } else {
          throw new Error(response.data?.message || "Save failed");
        }
      } catch (error) {
        console.error("Error saving daily schedule:", error);
        toast.error(
          error.response?.data?.message || error.message || "Failed to save daily schedule"
        );
      } finally {
        setSaving((prev) => ({ ...prev, daily: false }));
      }
      return;
    }

    await beginPracticeSave({
      saveKey: "daily",
      dataToSave,
      sections: { dailySchedule: true },
      successMessage: "Daily schedule saved successfully"
    });
  };

  const saveBloodPressure = async () => {
    if (draftMode) return;
    if (isSectionLocked('bp')) {
      toast.warning(
        isReadOnly
          ? "Practice is locked. Schedule and targets are view-only."
          : PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE
      );
      return;
    }
    if (!practiceId) {
      toast.error("Practice ID is required");
      return;
    }

    const changedPeriods = getChangedBpPeriodsPayload();
    const changedTargets = getChangedBpTargetsPayload();
    if (changedPeriods.length === 0 && changedTargets.length === 0) {
      toast.info("No blood pressure changes to save");
      return;
    }
    const dataToSave = {};
    if (changedPeriods.length > 0) dataToSave.bloodPressureTimePeriod = changedPeriods;
    if (changedTargets.length > 0) dataToSave.bloodPressureTargetRange = changedTargets;
    const sections = {
      bloodPressureTimePeriod: changedPeriods.length > 0,
      bloodPressureTargetRange: changedTargets.length > 0
    };

    if (patientId) {
      try {
        setSaving((prev) => ({ ...prev, bp: true }));
        const response = await patientService.updateScheduleAndTargets(
          practiceId,
          patientId,
          dataToSave
        );
        if (response.data?.success) {
          toast.success("Blood pressure settings saved successfully");
          await reloadFromServer();
          setUsePatientScheduleTargetsPreference(practiceId, patientId, true);
          if (typeof onSaved === "function") onSaved("bp");
        } else {
          throw new Error(response.data?.message || "Save failed");
        }
      } catch (error) {
        console.error("Error saving blood pressure settings:", error);
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to save blood pressure settings"
        );
      } finally {
        setSaving((prev) => ({ ...prev, bp: false }));
      }
      return;
    }

    await beginPracticeSave({
      saveKey: "bp",
      dataToSave,
      sections,
      successMessage: "Blood pressure settings saved successfully"
    });
  };

  const saveBloodGlucose = async () => {
    if (draftMode) return;
    if (isSectionLocked('bg')) {
      toast.warning(
        isReadOnly
          ? "Practice is locked. Schedule and targets are view-only."
          : PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE
      );
      return;
    }
    if (!practiceId) {
      toast.error("Practice ID is required");
      return;
    }

    const changedPeriods = getChangedBgPeriodsPayload();
    const changedTargets = getChangedBgTargetsPayload();
    if (changedPeriods.length === 0 && changedTargets.length === 0) {
      toast.info("No blood glucose changes to save");
      return;
    }
    const dataToSave = {};
    if (changedPeriods.length > 0) dataToSave.bloodGlucoseTimePeriod = changedPeriods;
    if (changedTargets.length > 0) dataToSave.bloodGlucoseTargetRange = changedTargets;
    const sections = {
      bloodGlucoseTimePeriod: changedPeriods.length > 0,
      bloodGlucoseTargetRange: changedTargets.length > 0
    };

    if (patientId) {
      try {
        setSaving((prev) => ({ ...prev, bg: true }));
        const response = await patientService.updateScheduleAndTargets(
          practiceId,
          patientId,
          dataToSave
        );
        if (response.data?.success) {
          toast.success("Blood glucose settings saved successfully");
          await reloadFromServer();
          setUsePatientScheduleTargetsPreference(practiceId, patientId, true);
          if (typeof onSaved === "function") onSaved("bg");
        } else {
          throw new Error(response.data?.message || "Save failed");
        }
      } catch (error) {
        console.error("Error saving blood glucose settings:", error);
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to save blood glucose settings"
        );
      } finally {
        setSaving((prev) => ({ ...prev, bg: false }));
      }
      return;
    }

    await beginPracticeSave({
      saveKey: "bg",
      dataToSave,
      sections,
      successMessage: "Blood glucose settings saved successfully"
    });
  };

  const saveWeight = async () => {
    if (draftMode) return;
    if (isSectionLocked('weight')) {
      toast.warning(
        isReadOnly
          ? "Practice is locked. Schedule and targets are view-only."
          : PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE
      );
      return;
    }
    if (!practiceId) {
      toast.error("Practice ID is required");
      return;
    }

    const changedPeriods = getChangedWeightPeriodsPayload();
    const changedTargets = getChangedWeightTargetsPayload();
    if (changedPeriods.length === 0 && !changedTargets) {
      toast.info("No weight changes to save");
      return;
    }
    const dataToSave = {};
    if (changedPeriods.length > 0) dataToSave.weightTimePeriod = changedPeriods;
    if (changedTargets) dataToSave.weightTargetRange = changedTargets;
    const sections = {
      weightTimePeriod: changedPeriods.length > 0,
      weightTargetRange: Boolean(changedTargets)
    };

    if (patientId) {
      try {
        setSaving((prev) => ({ ...prev, weight: true }));
        const response = await patientService.updateScheduleAndTargets(
          practiceId,
          patientId,
          dataToSave
        );
        if (response.data?.success) {
          toast.success("Weight settings saved successfully");
          await reloadFromServer();
          setUsePatientScheduleTargetsPreference(practiceId, patientId, true);
          if (typeof onSaved === "function") onSaved("weight");
        } else {
          throw new Error(response.data?.message || "Save failed");
        }
      } catch (error) {
        console.error("Error saving weight settings:", error);
        toast.error(
          error.response?.data?.message || error.message || "Failed to save weight settings"
        );
      } finally {
        setSaving((prev) => ({ ...prev, weight: false }));
      }
      return;
    }

    await beginPracticeSave({
      saveKey: "weight",
      dataToSave,
      sections,
      successMessage: "Weight settings saved successfully"
    });
  };

  // Reload the schedule/targets from the server and sync both the visible
  // React state and the initialData baseline. Called after every successful
  // save (so the UI always shows what's actually in the DB, including the
  // auto-increment row IDs assigned on INSERT) and from the Cancel button.
  //
  // This is critical on staging: previously we only updated `initialData`
  // from the locally-edited React state on save success, which kept the old
  // "React default" row IDs in state. On the next save those phantom IDs
  // would be sent back, miss the DB row, and the controller would INSERT
  // duplicate rows — making sections look "removed" / mis-aligned. Doing a
  // refetch closes that gap and also restores any other section the user
  // didn't touch (since the controller only updates sections present in the
  // payload, untouched data must come from the DB, not from stale state).
  const reloadFromServer = async () => {
    if (!practiceId) return;

    try {
      setLoading(true);
      let response;
      if (patientId) {
        response = await patientService.getScheduleAndTargets(practiceId, patientId);
      } else {
        response = await practiceService.getScheduleAndTargets(practiceId);
      }
      if (response.data?.success && response.data?.data) {
        const data = response.data.data;

        // Merge DB rows on top of the React-default rows (matching by name).
        // See the long comment in the initial-fetch useEffect for why this is
        // necessary — short version: the DB returns only the rows that have
        // been INSERTed, so a brand-new patient with one saved Bed Time would
        // otherwise collapse the visible list to 1 row.
        let resolvedDailySchedule = dailySchedule;
        if (data.dailySchedule && data.dailySchedule.length > 0) {
          const dbByName = mapByName(data.dailySchedule);
          resolvedDailySchedule = dailySchedule.map((defaultItem) => {
            const dbItem = dbByName.get(normalizeName(defaultItem.name));
            if (!dbItem) return defaultItem;
            return {
              ...defaultItem,
              id: dbItem.id ?? defaultItem.id,
              time: dbItem.time ? convertTimeTo12Hour(dbItem.time) : defaultItem.time
            };
          });
          setDailySchedule(resolvedDailySchedule);
        }

        let resolvedBpPeriods = bpPeriods;
        if (data.bloodPressureTimePeriod && data.bloodPressureTimePeriod.length > 0) {
          const dbByName = mapByName(data.bloodPressureTimePeriod);
          resolvedBpPeriods = bpPeriods.map((defaultItem) => {
            const dbItem = dbByName.get(normalizeName(defaultItem.name));
            if (!dbItem) return defaultItem;
            return {
              ...defaultItem,
              id: dbItem.id ?? defaultItem.id,
              start: BP_PERMANENT_START_TIMES[defaultItem.name] || (dbItem.startTime ? convertTimeTo12Hour(dbItem.startTime) : defaultItem.start),
              end: dbItem.endTime ? convertTimeTo12Hour(dbItem.endTime) : defaultItem.end,
              fixed: true
            };
          });
          setBpPeriods(resolvedBpPeriods);
        }

        let resolvedBpTargets = bpTargets;
        if (data.bloodPressureTargetRange && data.bloodPressureTargetRange.length > 0) {
          const dbByName = mapByName(data.bloodPressureTargetRange);
          resolvedBpTargets = bpTargets.map((defaultItem) => {
            const dbItem = dbByName.get(normalizeName(defaultItem.label));
            if (!dbItem) return defaultItem;
            return {
              ...defaultItem,
              id: dbItem.id ?? defaultItem.id,
              sysMin: dbItem.systolicMin ?? dbItem.sysMin ?? defaultItem.sysMin,
              sysMax: dbItem.systolicMax ?? dbItem.sysMax ?? defaultItem.sysMax,
              diaMin: dbItem.diastolicMin ?? dbItem.diaMin ?? defaultItem.diaMin,
              diaMax: dbItem.diastolicMax ?? dbItem.diaMax ?? defaultItem.diaMax,
              pulseMin: dbItem.pulseMin ?? dbItem.pulse_min ?? defaultItem.pulseMin,
              pulseMax: dbItem.pulseMax ?? dbItem.pulse_max ?? defaultItem.pulseMax
            };
          });
          setBpTargets(resolvedBpTargets);
        }

        let resolvedBgPeriods = bgPeriods;
        if (data.bloodGlucoseTimePeriod && data.bloodGlucoseTimePeriod.length > 0) {
          const dbByName = mapByName(data.bloodGlucoseTimePeriod);
          resolvedBgPeriods = bgPeriods.map((defaultItem) => {
            const dbItem = dbByName.get(normalizeName(defaultItem.name));
            if (!dbItem) return defaultItem;
            return {
              ...defaultItem,
              id: dbItem.id ?? defaultItem.id,
              start: BG_PERMANENT_START_TIMES[defaultItem.name] || (dbItem.startTime ? convertTimeTo12Hour(dbItem.startTime) : defaultItem.start),
              end: dbItem.endTime ? convertTimeTo12Hour(dbItem.endTime) : defaultItem.end,
              fixed: true
            };
          });
          setBgPeriods(resolvedBgPeriods);
        }

        let resolvedBgTargets = bgTargets;
        if (data.bloodGlucoseTargetRange && data.bloodGlucoseTargetRange.length > 0) {
          resolvedBgTargets = mergeBgTargetsFromDb(bgTargets, data.bloodGlucoseTargetRange);
          setBgTargets(resolvedBgTargets);
        }

        let resolvedWeightPeriods = weightPeriods;
        if (data.weightTimePeriod && data.weightTimePeriod.length > 0) {
          const dbByName = mapByName(data.weightTimePeriod);
          resolvedWeightPeriods = weightPeriods.map((defaultItem) => {
            const dbItem = dbByName.get(normalizeName(defaultItem.name));
            if (!dbItem) return defaultItem;
            return {
              ...defaultItem,
              id: dbItem.id ?? defaultItem.id,
              start: WEIGHT_PERMANENT_START_TIMES[defaultItem.name] || (dbItem.startTime ? convertTimeTo12Hour(dbItem.startTime) : defaultItem.start),
              end: dbItem.endTime ? convertTimeTo12Hour(dbItem.endTime) : defaultItem.end,
              fixed: true
            };
          });
          setWeightPeriods(resolvedWeightPeriods);
        }

        let resolvedWeightTargets = weightTargets;
        if (data.weightTargetRange && Object.keys(data.weightTargetRange).length > 0) {
          resolvedWeightTargets = {
            ...weightTargets,
            weightMin: normalizeNumericValue(data.weightTargetRange.weightMin, weightTargets.weightMin),
            weightMax: normalizeNumericValue(data.weightTargetRange.weightMax, weightTargets.weightMax),
            bodyFatMin: normalizeNumericValue(data.weightTargetRange.bodyFatMin, weightTargets.bodyFatMin),
            bodyFatMax: normalizeNumericValue(data.weightTargetRange.bodyFatMax, weightTargets.bodyFatMax),
            bmiUnderweight: data.weightTargetRange.bmiUnderweight ?? weightTargets.bmiUnderweight,
            bmiNormal: 18.5,
            bmiOverweight: 25,
            bmiObese: 30
          };
          setWeightTargets(resolvedWeightTargets);
        }

        savedSnapshotRef.current = {
          dailySchedule: JSON.parse(JSON.stringify(resolvedDailySchedule)),
          bpPeriods: JSON.parse(JSON.stringify(resolvedBpPeriods)),
          bpTargets: JSON.parse(JSON.stringify(resolvedBpTargets)),
          bgPeriods: JSON.parse(JSON.stringify(resolvedBgPeriods)),
          bgTargets: JSON.parse(JSON.stringify(resolvedBgTargets)),
          weightPeriods: JSON.parse(JSON.stringify(resolvedWeightPeriods)),
          weightTargets: JSON.parse(JSON.stringify(resolvedWeightTargets))
        };

        // Rebuild the baselines via the same merge logic as initial fetch so
        // unsaved-rows aren't falsely treated as dirty after reload. See the
        // long comment in the initial-fetch useEffect for the full rationale.
        const buildMergedBaseline = (defaults, dbRows, mapDefaultToBaseline, mapDbToBaseline) => {
          const dbByName = mapByName(dbRows);
          return defaults.map((d) => {
            const dbItem = dbByName.get(normalizeName(d?.name ?? d?.label));
            return dbItem ? mapDbToBaseline(d, dbItem) : mapDefaultToBaseline(d);
          });
        };

        setInitialData({
          dailySchedule: buildMergedBaseline(
            dailySchedule,
            data.dailySchedule,
            (s) => ({ id: s.id, name: s.name, time: convertTimeTo24Hour(s.time) }),
            (s, db) => ({ id: db.id ?? s.id, name: db.name ?? s.name, time: db.time })
          ),
          bpPeriods: buildMergedBaseline(
            bpPeriods,
            data.bloodPressureTimePeriod,
            (p) => ({
              id: p.id, name: p.name,
              startTime: convertTimeTo24Hour(p.start),
              endTime: convertTimeTo24Hour(p.end)
            }),
            (p, db) => ({
              id: db.id ?? p.id, name: db.name ?? p.name,
              startTime: db.startTime || convertTimeTo24Hour(p.start),
              endTime: db.endTime || convertTimeTo24Hour(p.end)
            })
          ),
          bpTargets: buildMergedBaseline(
            bpTargets,
            data.bloodPressureTargetRange,
            (t) => ({
              id: t.id, name: t.label,
              systolicMin: normalizeNumericValue(t.sysMin, 0),
              systolicMax: normalizeNumericValue(t.sysMax, 0),
              diastolicMin: normalizeNumericValue(t.diaMin, 0),
              diastolicMax: normalizeNumericValue(t.diaMax, 0),
              pulseMin: normalizeNumericValue(t.pulseMin, 60),
              pulseMax: normalizeNumericValue(t.pulseMax, 100)
            }),
            (t, db) => ({
              id: db.id ?? t.id, name: db.name ?? t.label,
              systolicMin: normalizeNumericValue(db.systolicMin, normalizeNumericValue(t.sysMin, 0)),
              systolicMax: normalizeNumericValue(db.systolicMax, normalizeNumericValue(t.sysMax, 0)),
              diastolicMin: normalizeNumericValue(db.diastolicMin, normalizeNumericValue(t.diaMin, 0)),
              diastolicMax: normalizeNumericValue(db.diastolicMax, normalizeNumericValue(t.diaMax, 0)),
              pulseMin: normalizeNumericValue(db.pulseMin, normalizeNumericValue(t.pulseMin, 60)),
              pulseMax: normalizeNumericValue(db.pulseMax, normalizeNumericValue(t.pulseMax, 100))
            })
          ),
          bgPeriods: buildMergedBaseline(
            bgPeriods,
            data.bloodGlucoseTimePeriod,
            (p) => ({
              id: p.id, name: p.name,
              startTime: convertTimeTo24Hour(p.start),
              endTime: convertTimeTo24Hour(p.end)
            }),
            (p, db) => ({
              id: db.id ?? p.id, name: db.name ?? p.name,
              startTime: db.startTime || convertTimeTo24Hour(p.start),
              endTime: db.endTime || convertTimeTo24Hour(p.end)
            })
          ),
          bgTargets: buildBgTargetsBaseline(
            bgTargets,
            data.bloodGlucoseTargetRange
          ),
          weightPeriods: buildMergedBaseline(
            weightPeriods,
            data.weightTimePeriod,
            (p) => ({
              id: p.id, name: p.name,
              startTime: convertTimeTo24Hour(p.start),
              endTime: convertTimeTo24Hour(p.end)
            }),
            (p, db) => ({
              id: db.id ?? p.id, name: db.name ?? p.name,
              startTime: db.startTime || convertTimeTo24Hour(p.start),
              endTime: db.endTime || convertTimeTo24Hour(p.end)
            })
          ),
          weightTargetRange: (data.weightTargetRange && Object.keys(data.weightTargetRange).length > 0)
            ? JSON.parse(JSON.stringify(data.weightTargetRange))
            : {
                weightMin: normalizeNumericValue(weightTargets.weightMin, 66),
                weightMax: normalizeNumericValue(weightTargets.weightMax, 220),
                bodyFatMin: normalizeNumericValue(weightTargets.bodyFatMin, 14),
                bodyFatMax: normalizeNumericValue(weightTargets.bodyFatMax, 27),
                bmiUnderweight: weightTargets.bmiUnderweight || "",
                bmiNormal: 18.5, bmiOverweight: 25, bmiObese: 30
              }
        });
      }
    } catch (error) {
      console.error("Error reloading schedule/targets from server:", error);
      toast.error("Failed to reload data from database");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (section) => {
    restoreFromBaseline(section);
  };

  // DIRTY CHECKS
  const isDailyDirty = () => {
    if (!initialData) return false;
    const current = dailySchedule.map(s => ({ ...s, time: convertTimeTo24Hour(s.time) }));
    const initial = initialData.dailySchedule.map(s => ({ 
      id: s.id, 
      name: s.name, 
      time: convertTimeTo24Hour(s.time)
    }));

    return JSON.stringify(current) !== JSON.stringify(initial);
  };

  const isBpDirty = () => {
    if (!initialData) return false;
    const currentPeriods = bpPeriods.map(p => ({ 
      id: p.id, 
      name: p.name, 
      endTime: convertTimeTo24Hour(p.end) 
    }));
    const initialPeriods = initialData.bpPeriods.map(p => ({ 
      id: p.id, 
      name: p.name, 
      endTime: convertTimeTo24Hour(p.endTime || p.end) 
    }));

    
    const currentTargets = bpTargets.map(t => ({
      id: t.id,
      name: t.label,
      systolicMin: normalizeNumericValue(t.sysMin, 0),
      systolicMax: normalizeNumericValue(t.sysMax, 0),
      diastolicMin: normalizeNumericValue(t.diaMin, 0),
      diastolicMax: normalizeNumericValue(t.diaMax, 0),
      pulseMin: normalizeNumericValue(t.pulseMin, 60),
      pulseMax: normalizeNumericValue(t.pulseMax, 100)
    }));
    const initialTargets = initialData.bpTargets.map(t => ({
      id: t.id,
      name: t.name,
      systolicMin: normalizeNumericValue(t.systolicMin ?? t.sysMin, 0),
      systolicMax: normalizeNumericValue(t.systolicMax ?? t.sysMax, 0),
      diastolicMin: normalizeNumericValue(t.diastolicMin ?? t.diaMin, 0),
      diastolicMax: normalizeNumericValue(t.diastolicMax ?? t.diaMax, 0),
      pulseMin: normalizeNumericValue(t.pulseMin, 60),
      pulseMax: normalizeNumericValue(t.pulseMax, 100)
    }));

    return JSON.stringify(currentPeriods) !== JSON.stringify(initialPeriods) || 
           JSON.stringify(currentTargets) !== JSON.stringify(initialTargets);
  };

  const isBgDirty = () => {
    if (!initialData) return false;
    const currentPeriods = bgPeriods.map(p => ({ 
      id: p.id, 
      name: p.name, 
      endTime: convertTimeTo24Hour(p.end) 
    }));
    const initialPeriods = initialData.bgPeriods.map(p => ({ 
      id: p.id, 
      name: p.name, 
      endTime: convertTimeTo24Hour(p.endTime || p.end) 
    }));

    
    const currentTargets = bgTargets.map(t => ({
      id: t.id,
      name: t.label,
      minimum: normalizeNumericValue(t.min, 0),
      maximum: normalizeNumericValue(t.max, 0)
    }));
    const initialTargets = initialData.bgTargets.map(t => ({
      id: t.id,
      name: t.name,
      minimum: normalizeNumericValue(t.minimum ?? t.min, 0),
      maximum: normalizeNumericValue(t.maximum ?? t.max, 0)
    }));

    return JSON.stringify(currentPeriods) !== JSON.stringify(initialPeriods) || 
           JSON.stringify(currentTargets) !== JSON.stringify(initialTargets);
  };

  const isWeightDirty = () => {
    if (!initialData) return false;
    const currentPeriods = weightPeriods.map(p => ({ 
      id: p.id, 
      name: p.name, 
      startTime: convertTimeTo24Hour(p.start), 
      endTime: convertTimeTo24Hour(p.end) 
    }));
    const initialPeriods = initialData.weightPeriods.map(p => ({ 
      id: p.id, 
      name: p.name, 
      startTime: convertTimeTo24Hour(p.startTime || p.start), 
      endTime: convertTimeTo24Hour(p.endTime || p.end) 
    }));


    const currentTargets = {
      weightMin: weightTargets.weightMin,
      weightMax: weightTargets.weightMax,
      bodyFatMin: weightTargets.bodyFatMin,
      bodyFatMax: weightTargets.bodyFatMax,
      bmiUnderweight: weightTargets.bmiUnderweight,
      bmiNormal: weightTargets.bmiNormal,
      bmiOverweight: weightTargets.bmiOverweight,
      bmiObese: weightTargets.bmiObese
    };
    const initialTargets = initialData.weightTargetRange;

    const normalizedInitial = {
      weightMin: normalizeNumericValue(initialTargets.weightMin, 66),
      weightMax: normalizeNumericValue(initialTargets.weightMax, 220),
      bodyFatMin: normalizeNumericValue(initialTargets.bodyFatMin, 14),
      bodyFatMax: normalizeNumericValue(initialTargets.bodyFatMax, 27),
      bmiUnderweight: initialTargets.bmiUnderweight || "",
      bmiNormal: 18.5,
      bmiOverweight: 25,
      bmiObese: 30
    };

    return JSON.stringify(currentPeriods) !== JSON.stringify(initialPeriods) || 
           JSON.stringify(currentTargets) !== JSON.stringify(normalizedInitial);
  };

  // Initial DB fetch: show spinner until real values load (avoid editing stale defaults).
  if (isInitialLoading) {
    return (
      <div
        className={`schedule-container${isReadOnly ? " schedule-container--readonly" : ""}`}
        aria-busy="true"
        aria-live="polite"
      >
        <PageLoadingSpinner />
      </div>
    );
  }

  return (
    <>
    <div className={`schedule-container${isReadOnly ? ' schedule-container--readonly' : ''}`}>
      {isReadOnly && (
        <div className="alert alert-warning py-2 mb-3" role="alert">
          {readOnlyMessage}
        </div>
      )}

      <nav className="sts-subnav" aria-label="Schedule and target categories">
        {SUBNAV_ITEMS.map((item) => {
          const enabled = sectionEnabled[item.id] !== false;
          const viewOnly = enabled && isSectionLocked(item.id) && !isReadOnly;
          const isActive = enabled && activePanel === item.id;
          return (
            <button
              key={item.id}
              type="button"
              className={`sts-subnav-item${isActive ? ' active' : ''}${!enabled ? ' is-disabled' : ''}`}
              onClick={() => {
                if (!enabled) return;
                handleTabChange(item.id);
              }}
              disabled={!enabled}
              title={
                !enabled
                  ? (patientId ? 'Not assigned for this patient' : 'Not enrolled for this practice')
                  : viewOnly
                    ? PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE
                    : undefined
              }
              aria-pressed={isActive}
              aria-disabled={!enabled}
            >
              {item.label}{viewOnly ? ' (view only)' : ''}
            </button>
          );
        })}
      </nav>

      {/* ===== DAILY SCHEDULE ===== */}
      <div className={`sts-panel${activePanel === 'daily' ? ' active' : ''}`} id="sts-panel-daily">
        <PanelIntro
          title="Daily Schedule"
        />
        <div className="sts-daily-row">
          {dailySchedule.map((item, i) => (
            <div key={item.id} className="sts-daily-card">
              <div className="sts-daily-card-icon">{DAILY_CARD_ICONS[i]}</div>
              <p className="sts-daily-card-label">
                {formatDailyCardLabel(item.name)} <span className="req">*</span>
              </p>
              <div className="sts-daily-card-time">
                <TimeInputAMPM
                  className="time-input daily-time-input"
                  value={convertTimeTo24Hour(item.time)}
                  onChange={(e) => updateDaily(i, convertTimeTo12Hour(e.target.value))}
                  disabled={isReadOnly}
                />
              </div>
            </div>
          ))}
        </div>
        {!draftMode && (
        <div className="btn-group">
          <button className="btn-cancel" onClick={() => handleCancel('daily')} disabled={isReadOnly || isAnySaving || saving.daily || !isDailyDirty()}>
            Cancel
          </button>
          <button className="btn-save" onClick={saveDailySchedule} disabled={isReadOnly || isAnySaving || saving.daily || !isDailyDirty()}>
            {saving.daily ? "Saving..." : "Save"}
          </button>
        </div>
        )}
      </div>

      {/* ===== BLOOD PRESSURE ===== */}
      <div className={`sts-panel${activePanel === 'bp' ? ' active' : ''}${bpLocked ? ' is-view-only' : ''}`} id="sts-panel-bp">
        <PanelIntro
          title="Blood Pressure Targets"
        />
        {bpLocked && !isReadOnly && (
          <div className="alert alert-warning py-2 mb-3" role="alert">
            {PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE}
          </div>
        )}
        <div className="sts-table-wrap">
          <table className="sts-combo-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Reading Window</th>
                <th>Systolic Target</th>
                <th>Diastolic Target</th>
                <th>Pulse Target</th>
              </tr>
            </thead>
            <tbody>
              {bpPeriods.map((p, i) => (
                <tr key={p.id}>
                  <td className="sts-period-name">{p.name}</td>
                  <td>
                    <div className="sts-time-window">
                      <div className="range-time sts-table-range-time">
                        <div className="fixed-time-display">{p.start}</div>
                        <span className="dash">–</span>
                        <div className="time-wrapper">
                          <TimeInputAMPM
                            className="time-input"
                            value={convertTimeTo24Hour(p.end)}
                            disabled={bpLocked}
                            onChange={(e) => updateBpPeriod(i, 'end', convertTimeTo12Hour(e.target.value))}
                          />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="num-range sts-table-num-range">
                      <input
                        type="number"
                        value={bpTargets[i].sysMin}
                        onChange={(e) => updateBpTarget(i, 'sysMin', e.target.value)}
                        min="0"
                        max="300"
                        disabled={bpLocked}
                        title={bpLocked && !isReadOnly ? PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE : undefined}
                        aria-label={`${p.name} systolic minimum`}
                      />
                      <span className="dash">–</span>
                      <input
                        type="number"
                        value={bpTargets[i].sysMax}
                        onChange={(e) => updateBpTarget(i, 'sysMax', e.target.value)}
                        min="0"
                        max="300"
                        disabled={bpLocked}
                        title={bpLocked && !isReadOnly ? PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE : undefined}
                        aria-label={`${p.name} systolic maximum`}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="num-range sts-table-num-range">
                      <input
                        type="number"
                        value={bpTargets[i].diaMin}
                        onChange={(e) => updateBpTarget(i, 'diaMin', e.target.value)}
                        min="0"
                        max="200"
                        disabled={bpLocked}
                        title={bpLocked && !isReadOnly ? PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE : undefined}
                        aria-label={`${p.name} diastolic minimum`}
                      />
                      <span className="dash">–</span>
                      <input
                        type="number"
                        value={bpTargets[i].diaMax}
                        onChange={(e) => updateBpTarget(i, 'diaMax', e.target.value)}
                        min="0"
                        max="200"
                        disabled={bpLocked}
                        title={bpLocked && !isReadOnly ? PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE : undefined}
                        aria-label={`${p.name} diastolic maximum`}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="num-range sts-table-num-range">
                      <input
                        type="number"
                        value={bpTargets[i].pulseMin}
                        onChange={(e) => updateBpTarget(i, 'pulseMin', e.target.value)}
                        min="0"
                        max="300"
                        disabled={bpLocked}
                        title={bpLocked && !isReadOnly ? PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE : undefined}
                        aria-label={`${p.name} pulse minimum`}
                      />
                      <span className="dash">–</span>
                      <input
                        type="number"
                        value={bpTargets[i].pulseMax}
                        onChange={(e) => updateBpTarget(i, 'pulseMax', e.target.value)}
                        min="0"
                        max="300"
                        disabled={bpLocked}
                        title={bpLocked && !isReadOnly ? PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE : undefined}
                        aria-label={`${p.name} pulse maximum`}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!draftMode && (
        <div className="btn-group">
          <button className="btn-cancel" onClick={() => handleCancel('bp')} disabled={bpLocked || isAnySaving || saving.bp || !isBpDirty()}>
            Cancel
          </button>
          <button className="btn-save" onClick={saveBloodPressure} disabled={bpLocked || isAnySaving || saving.bp || !isBpDirty()}>
            {saving.bp ? "Saving..." : "Save"}
          </button>
        </div>
        )}
      </div>

      {/* ===== BLOOD GLUCOSE ===== */}
      <div className={`sts-panel${activePanel === 'bg' ? ' active' : ''}${bgLocked ? ' is-view-only' : ''}`} id="sts-panel-bg">
        <PanelIntro
          title="Blood Glucose Targets"
        />
        {bgLocked && !isReadOnly && (
          <div className="alert alert-warning py-2 mb-3" role="alert">
            {PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE}
          </div>
        )}
        <div className="sts-table-wrap">
          <table className="sts-combo-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Reading Window</th>
                <th>Target Range (mg/dL)</th>
              </tr>
            </thead>
            <tbody>
              {bgPeriods.map((p, i) => {
                const target = bgTargets[i];
                return (
                  <tr key={p.id}>
                    <td className="sts-period-name">{p.name}</td>
                    <td>
                      <div className="sts-time-window">
                        <div className="range-time sts-table-range-time">
                          <div className="fixed-time-display">{p.start}</div>
                          <span className="dash">–</span>
                          <div className="time-wrapper">
                            <TimeInputAMPM
                              className="time-input"
                              value={convertTimeTo24Hour(p.end)}
                              disabled={bgLocked}
                              onChange={(e) => updateBgPeriod(i, 'end', convertTimeTo12Hour(e.target.value))}
                            />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="num-range sts-table-num-range">
                        <input
                          type="number"
                          value={target?.min ?? ""}
                          onChange={(e) => updateBgTarget(i, 'min', e.target.value)}
                          min="0"
                          max="300"
                          disabled={bgLocked}
                          title={bgLocked && !isReadOnly ? PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE : undefined}
                          aria-label={`${p.name} target minimum`}
                        />
                        <span className="dash">–</span>
                        <input
                          type="number"
                          value={target?.max ?? ""}
                          onChange={(e) => updateBgTarget(i, 'max', e.target.value)}
                          min="0"
                          max="300"
                          disabled={bgLocked}
                          title={bgLocked && !isReadOnly ? PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE : undefined}
                          aria-label={`${p.name} target maximum`}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!draftMode && (
        <div className="btn-group">
          <button className="btn-cancel" onClick={() => handleCancel('bg')} disabled={bgLocked || isAnySaving || saving.bg || !isBgDirty()}>
            Cancel
          </button>
          <button className="btn-save" onClick={saveBloodGlucose} disabled={bgLocked || isAnySaving || saving.bg || !isBgDirty()}>
            {saving.bg ? "Saving..." : "Save"}
          </button>
        </div>
        )}
      </div>

      {/* ===== WEIGHT ===== */}
      <div className={`sts-panel${activePanel === 'weight' ? ' active' : ''}${weightLocked ? ' is-view-only' : ''}`} id="sts-panel-weight">
        <PanelIntro
          title="Weight Targets"
        />
        {weightLocked && !isReadOnly && (
          <div className="alert alert-warning py-2 mb-3" role="alert">
            {PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE}
          </div>
        )}

        <div className="sts-info-banner">
          <div className="sts-info-banner-item">
            <p className="sts-info-banner-label">Recommended Weight Range</p>
            <p className="sts-info-banner-value">-- lb</p>
          </div>
          <div className="sts-info-banner-item">
            <p className="sts-info-banner-label">Recommended Body Fat Range</p>
            <p className="sts-info-banner-value">-- %</p>
          </div>
        </div>

        <div className="sts-target-row">
          <div className="sts-target-card">
            <p className="sts-target-card-label">
              Weight Target Range (lb) <span className="req">*</span>
            </p>
            <div className="num-range sts-range-input-row">
              <input
                type="number"
                value={weightTargets.weightMin}
                onChange={(e) => updateWeightTarget('weightMin', e.target.value)}
                min="0"
                max="1000"
                disabled={weightLocked}
                title={weightLocked && !isReadOnly ? PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE : undefined}
                aria-label="Weight minimum"
              />
              <span className="dash">–</span>
              <input
                type="number"
                value={weightTargets.weightMax}
                onChange={(e) => updateWeightTarget('weightMax', e.target.value)}
                min="0"
                max="1000"
                disabled={weightLocked}
                title={weightLocked && !isReadOnly ? PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE : undefined}
                aria-label="Weight maximum"
              />
            </div>
          </div>
          <div className="sts-target-card">
            <p className="sts-target-card-label">
              Body Fat Target Range (%) <span className="req">*</span>
            </p>
            <div className="num-range sts-range-input-row">
              <input
                type="number"
                value={weightTargets.bodyFatMin}
                onChange={(e) => updateWeightTarget('bodyFatMin', e.target.value)}
                min="0"
                max="100"
                disabled={weightLocked}
                title={weightLocked && !isReadOnly ? PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE : undefined}
                aria-label="Body fat minimum"
              />
              <span className="dash">–</span>
              <input
                type="number"
                value={weightTargets.bodyFatMax}
                onChange={(e) => updateWeightTarget('bodyFatMax', e.target.value)}
                min="0"
                max="100"
                disabled={weightLocked}
                title={weightLocked && !isReadOnly ? PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE : undefined}
                aria-label="Body fat maximum"
              />
            </div>
          </div>
        </div>

        <p className="sts-section-label">BMI Reference</p>
        <div className="sts-bmi-strip">
          <div className="sts-bmi-chip sts-bmi-chip--under">
            <p className="sts-bmi-chip-label">Underweight</p>
            <input
              type="text"
              className="sts-bmi-chip-input sts-bmi-chip-input--readonly"
              value="< 18.5"
              readOnly
              tabIndex={-1}
              aria-label="Underweight BMI range"
            />
          </div>
          <div className="sts-bmi-chip sts-bmi-chip--normal">
            <p className="sts-bmi-chip-label">Normal Weight</p>
            <input
              type="text"
              className="sts-bmi-chip-input sts-bmi-chip-input--readonly"
              value="18.5 – 24.9"
              readOnly
              tabIndex={-1}
              aria-label="Normal Weight BMI range"
            />
          </div>
          <div className="sts-bmi-chip sts-bmi-chip--over">
            <p className="sts-bmi-chip-label">Overweight</p>
            <input
              type="text"
              className="sts-bmi-chip-input sts-bmi-chip-input--readonly"
              value="25.0 – 29.9"
              readOnly
              tabIndex={-1}
              aria-label="Overweight BMI range"
            />
          </div>
          <div className="sts-bmi-chip sts-bmi-chip--obese">
            <p className="sts-bmi-chip-label">Obese</p>
            <input
              type="text"
              className="sts-bmi-chip-input sts-bmi-chip-input--readonly"
              value="≥ 30.0"
              readOnly
              tabIndex={-1}
              aria-label="Obese BMI range"
            />
          </div>
        </div>

        <p className="sts-section-label">Monitoring Windows</p>
        <div className="sts-table-wrap">
          <table className="sts-combo-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Reading Window</th>
              </tr>
            </thead>
            <tbody>
              {weightPeriods.map((p, i) => (
                <tr key={p.id}>
                  <td className="sts-period-name">{p.name}</td>
                  <td>
                    <div className="sts-time-window">
                      <div className="range-time sts-table-range-time">
                        <div className="fixed-time-display">{p.start}</div>
                        <span className="dash">–</span>
                        <div className="time-wrapper">
                          <TimeInputAMPM
                            className="time-input"
                            value={convertTimeTo24Hour(p.end)}
                            disabled={weightLocked}
                            onChange={(e) => updateWeightPeriod(i, 'end', convertTimeTo12Hour(e.target.value))}
                          />
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!draftMode && (
        <div className="btn-group">
          <button className="btn-cancel" onClick={() => handleCancel('weight')} disabled={weightLocked || isAnySaving || saving.weight || !isWeightDirty()}>
            Cancel
          </button>
          <button className="btn-save" onClick={saveWeight} disabled={weightLocked || isAnySaving || saving.weight || !isWeightDirty()}>
            {saving.weight ? "Saving..." : "Save"}
          </button>
        </div>
        )}
      </div>
    </div>

    {showSectionEventLogButtons && !draftMode && (
      <div className="sts-event-log-actions">
        <button
          type="button"
          className="pd-logs-btn patient-details-event-log-tab-btn sts-events-log-btn"
          onClick={() => handleEventLog(getActiveSectionForEventLog())}
          disabled={isAnySaving}
        >
          <FontAwesomeIcon icon={faList} className="patient-details-event-log-tab-btn__icon sts-event-log-icon" />
          Event Logs
        </button>
      </div>
    )}

    <ApplyScheduleTargetsToPatientsModal
      isOpen={applyPatientsModal.open}
      matchingCount={(applyPatientsModal.patients || []).length}
      sectionLabel={applyPatientsModal.sectionLabel}
      applying={applyingToPatients}
      onClose={handleCloseApplyPatientsModal}
      onConfirm={handleConfirmApplyToPatients}
    />
    </>
  );
});

export default ScheduleTargetSettings;
